import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { trainingGroupsAPI } from '../../services/api';
import { useNotifications } from '../notifications/NotificationProvider';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ReminderSettingsModal = ({ show, onClose, trainingGroup }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();
  const [formData, setFormData] = useState({
    availability_reminder_enabled: true,
    availability_reminder_day: 6, // Default to Sunday
  });

  // Initialize form data when training group changes
  useEffect(() => {
    if (trainingGroup) {
      setFormData({
        availability_reminder_enabled: trainingGroup.availability_reminder_enabled ?? true,
        availability_reminder_day: trainingGroup.availability_reminder_day ?? 6,
      });
    }
  }, [trainingGroup]);

  const updateGroupMutation = useMutation(
    ({ groupId, data }) => trainingGroupsAPI.updateGroup(groupId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['trainingGroup', trainingGroup?.id]);
        queryClient.invalidateQueries('trainingGroups');
        showSuccess('Reminder settings updated successfully');
        onClose();
      },
      onError: (error) => {
        showError(
          `Failed to update reminder settings: ${error.response?.data?.detail || error.message}`
        );
      },
    }
  );

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (trainingGroup) {
      updateGroupMutation.mutate({
        groupId: trainingGroup.id,
        data: formData,
      });
    }
  };

  const getDayName = (dayNumber) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayNumber] || 'Sunday';
  };

  if (!show || !trainingGroup) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h3 className="text-lg font-medium text-gray-900">
              Reminder Settings - {trainingGroup.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={updateGroupMutation.isLoading}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-4">
            <div className="space-y-6">
              {/* Enable/Disable Reminders */}
              <div className="flex items-center">
                <input
                  id="availability_reminder_enabled"
                  name="availability_reminder_enabled"
                  type="checkbox"
                  checked={formData.availability_reminder_enabled}
                  onChange={handleFormChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="availability_reminder_enabled"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Enable automatic availability reminders
                </label>
              </div>

              {/* Reminder Day Selection */}
              {formData.availability_reminder_enabled && (
                <div>
                  <label
                    htmlFor="availability_reminder_day"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Reminder Day
                  </label>
                  <select
                    id="availability_reminder_day"
                    name="availability_reminder_day"
                    value={formData.availability_reminder_day}
                    onChange={handleFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value={0}>Monday</option>
                    <option value={1}>Tuesday</option>
                    <option value={2}>Wednesday</option>
                    <option value={3}>Thursday</option>
                    <option value={4}>Friday</option>
                    <option value={5}>Saturday</option>
                    <option value={6}>Sunday</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Members will receive reminders to set their availability on{' '}
                    {getDayName(formData.availability_reminder_day)}s
                  </p>
                </div>
              )}

              {/* Info Section */}
              <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      These settings control when members receive automatic reminders to set their
                      availability for upcoming training sessions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={updateGroupMutation.isLoading}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateGroupMutation.isLoading}
                className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {updateGroupMutation.isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReminderSettingsModal;
